import { useEffect, useState, useRef } from "react";
import { styleSearch } from "../../api/GetJewellery";
import { useFilters } from "../../context/FilterContext";
import ProductCard from "./ProductCards";
import { useAuth } from "../../context/AuthContext";
import { Dialog } from 'primereact/dialog';
import { Link } from "react-router-dom";

export default function VaultCards({ products, setProducts, setTotalProducts }) {
    const DEFAULT_IMAGE = "/assets/images/VaultAccess/no-image3.png";
    const { user } = useAuth();
    const { selectedFilters, selectedCategory, sort, hasMore, setContextData, getFormattedFilters } = useFilters();

    const [page, setPage] = useState(1);
    const [isFetching, setIsFetching] = useState(false);

    const loaderRef = useRef(null);
    const pageSize = 50;
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenQuickView = (productDetails) => {
        setSelectedProduct(productDetails);
        setIsModalOpen(true);
    };

    const handleCloseQuickView = () => {
        setSelectedProduct(null);
        setIsModalOpen(false);
    };

    const isInvalid = (val) => val === null || val === undefined || val === "" || Number(val) === 0;

    const formatPcsCts = (pcs, wt) => {
        return isInvalid(pcs) || isInvalid(wt) ? "-" : `${pcs} Pcs / ${wt} Cts`;
    };

    const showValue = (value) => {
        if (value === null || value === undefined || value === "" || Number(value) === 0) {
            return null;
        }
        return value;
    };

    const fetchProducts = async (pageNo = 1) => {
        if (isFetching) return;

        setIsFetching(true);
        try {
            const filterPayload = getFormattedFilters();
            const payload = {
                Party_ID: String(user.SelectedCustomer_ID || selectedFilters.Party_ID || "0"),
                Category: String(selectedCategory || ""),
                SubCategory: "",
                Metal: (selectedFilters["Metal Tone"] || []).map(x => x.value).join("~"),
                GoldQuality: (selectedFilters["Gold Quality"] || []).map(x => x.value).join("~"),
                DiamondShape: (selectedFilters["Diamond Shape"] || []).map(x => x.value).join("~"),
                DiamondQuality: (selectedFilters["Diamond Quality"] || []).map(x => x.value).join("~"),
                Collection: (selectedFilters["Collection"] || []).map(x => x.value).join("~"),
                ItemSize: (selectedFilters["Item Size"] || []).map(x => x.value).join("~"),
                StyleNo: String(selectedFilters["By Ring Style"] || ""),
                FromPrice: String(selectedFilters["Price"]?.from ?? "0"),
                ToPrice: String(selectedFilters["Price"]?.to ?? "10000000"),
                FromCarat: String(selectedFilters["Carat"]?.from ?? "0"),
                ToCarat: String(selectedFilters["Carat"]?.to ?? "100"),
                FromGoldWeight: String(selectedFilters["Gold Weight"]?.from ?? "0"),
                ToGoldWeight: String(selectedFilters["Gold Weight"]?.to ?? "250"),
                PageNo: String(pageNo),
                PageSize: String(pageSize),
                Style_ID: "",
                SortBy: String(sort) || "",
            };
            const response = await styleSearch(payload);
            const list = response?.Products || [];
            const totalRecords = response?.Pagination?.[0]?.Total_Record || 0;
            const totalPages = response?.Pagination?.[0]?.Total_Page || 1;

            setTotalProducts(totalRecords);

            setProducts(prev =>
                pageNo === 1 ? list : [...prev, ...list]
            );

            setContextData({
                hasMore: pageNo < totalPages
            });

        } catch (error) {
            console.log("Fetch error", error);
        } finally {
            setIsFetching(false);
        }
    };

    const didInitialFetch = useRef(false);

    useEffect(() => {
        setProducts([]);
        setPage(1);
        fetchProducts(1);
    }, [selectedFilters, selectedCategory, sort]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isFetching) {
                    setPage(prevPage => {
                        const nextPage = prevPage + 1;
                        fetchProducts(nextPage);
                        return nextPage;
                    });
                }
            },
            { rootMargin: "200px" }
        );
        const current = loaderRef.current;
        if (current) observer.observe(current);
        return () => {
            if (current) observer.unobserve(current);
        };
    }, [hasMore, isFetching]);

    const ProductSkeleton = () => (
        <div className="product-card skeleton-card">
            <div className="skeleton skeleton-image"></div>
            <div className="skeleton skeleton-text small"></div>
            <div className="skeleton skeleton-text"></div>
            <div className="skeleton skeleton-text small"></div>
            <div className="skeleton skeleton-btn"></div>
        </div>
    );

    return (
        <div className="vault-access-container">
            <div className="product-grid">
                {products.map((item, index) => (
                    <ProductCard key={`${item.Style_ID}-${index}`} product={item} onQuickViewOpen={handleOpenQuickView} />
                ))}

                {isFetching && Array.from({ length: 12 }).map((_, i) => ( <ProductSkeleton key={i} /> ))}
            </div>

            <Dialog
                visible={isModalOpen}
                className="quickview-dialog"
                onHide={() => setIsModalOpen(false)}
                draggable={false}
                dismissableMask
                showHeader={false}
                breakpoints={{ '960px': '80vw', '641px': '95vw' }}
            >
                {selectedProduct && (
                    <div className="qv-container">
                        <div className="qv-image-section">
                            <img src={selectedProduct.Image1 || DEFAULT_IMAGE} alt={selectedProduct.StyleNo} className="qv-main-img" />
                        </div>

                        <div className="qv-details-section">
                            <div className="qv-heading">Quick View: </div>
                            <div className="qv-ref">
                                <label>Style No: </label>
                                <span> {selectedProduct.StyleNo}</span>
                            </div>
                            <div className="qv-ref">
                                <span> {selectedProduct.LineAmount}</span>
                            </div>
                            <div className="qv-header-row">
                                <label>Product Name: </label>
                                <h2 className="qv-title">{selectedProduct.Line1}</h2>
                            </div>

                            <hr className="qv-divider" />

                            <div className="qv-grid-info">
                                <div className="qv-info-item full-width">
                                    <label>GOLD PURITY</label>
                                    <span>{selectedProduct.MetalQuality || "-"} / {selectedProduct.MetalTone || "-"}</span>
                                </div>
                                <div className="qv-info-item full-width">
                                    <label>GOLD WEIGHT</label>
                                    <span>{selectedProduct.TotalNetWt || "-"} G</span>
                                </div>
                                <div className="qv-info-item full-width">
                                    <label>DIMENSION</label>
                                    <span>{selectedProduct.ItemSize || "-"}</span>
                                </div>
                            </div>

                            <div className="qv-footer">
                                <Link to={`/product-detail/${selectedProduct.Style_ID}`} className="qv-details-link">
                                    VIEW MORE DETAILS <span className="qv-arrow">→</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
            <div ref={loaderRef}></div>
        </div>
    );
}